import React from 'react';

import { Polygon3D } from '@paragon/geometry-library';
import { BoundedPlaneShape, DisplayablePlaneWithCalculatedGeometry } from '@paragon/layout';
import { RaycastOrder } from '@paragon/three-shared';

import { useAppSelector } from '../../../../components/hooks';
import { is3DLayoutCameraInUse } from '../../../../store/selectors/layoutModeSelectors';
import { LayoutPolygonVisualMode } from '../../polygon-rendering/common/mode';
import { LayoutPolygonStyle } from '../../polygon-rendering/common/style';
import { PlaneDefinitionVisuals } from './definitions/PlaneDefinitionVisuals';
import { PlaneEventHandlers } from './PlaneEventHandlers';
import { PlaneFaceVisual } from './PlaneFaceVisual';

interface Props {
  plane: DisplayablePlaneWithCalculatedGeometry;

  style: LayoutPolygonStyle;
  mode: LayoutPolygonVisualMode;

  pickable: boolean;

  calculatedBounds?: BoundedPlaneShape;
  overhangRegions?: Polygon3D[] | null;
  subfasciaRegions?: Polygon3D[] | null;
}

export function PlaneVisual({
  plane,
  style,
  mode,
  pickable,
  calculatedBounds,
  overhangRegions,
  subfasciaRegions,
}: Props) {
  const threeDimensional = useAppSelector(is3DLayoutCameraInUse);

  const showDefinitions = mode === 'translucent';
  const showPlanes = mode === 'solid' || (mode === 'translucent' && threeDimensional);
  const showFaceHitBox = pickable && !showPlanes;

  return (
    <PlaneEventHandlers plane={plane}>
      {showDefinitions && (
        <PlaneDefinitionVisuals
          plane={plane}
          style={style}
          calculatedBounds={calculatedBounds}
          raycastOrder={pickable ? RaycastOrder.PickablePlaneDefinitionVisual : RaycastOrder.Default}
        />
      )}

      {(showPlanes || showFaceHitBox) && calculatedBounds && !('segments' in calculatedBounds) && (
        <PlaneFaceVisual
          planeGeometry={calculatedBounds}
          overhangGeometry={overhangRegions}
          subfasciaGeometry={subfasciaRegions}
          visible={showPlanes}
          style={style}
          mode={mode}
        />
      )}
    </PlaneEventHandlers>
  );
}
